import { brl, pct } from '../../lib/format'
import { color, DISCOUNT_SCALE, radius, shadow } from '../../theme/tokens'
import { Eyebrow, Pill } from '../ui'

/**
 * Envelope de negociação.
 *
 * A régua mostra, em uma imagem só: o teto autorizado pela política, onde a IA
 * está, onde o cliente pediu, e o caminho já percorrido. É a peça que torna
 * "a IA respeitou a política" visível sem ler log nenhum.
 */
export function NegotiationEnvelope({
  debt,
  capPct,
  offerPct,
  clientAskPct,
  trail,
  modalityLabel,
  offerWall,
}: {
  debt: number
  capPct: number
  offerPct: number | null
  clientAskPct: number | null
  trail: number[]
  modalityLabel: string
  offerWall: boolean
}) {
  const x = (value: number) => `${(Math.min(value, DISCOUNT_SCALE) / DISCOUNT_SCALE) * 100}%`

  const capWidth = x(capPct)
  const capValue = debt * (1 - capPct / 100)
  const margin = offerPct === null ? null : (debt * (capPct - offerPct)) / 100

  // Teto zero significa que nenhuma linha da matriz cobre o caso (90+ dias):
  // não é "margem esgotada", é ausência de autorização para negociar.
  const blocked = capPct <= 0

  const marginLabel = blocked
    ? 'Sem faixa autorizada · nenhuma oferta permitida'
    : margin === null
      ? 'Aguardando primeira oferta'
      : margin > 0
        ? `Margem preservada: ${brl(margin)}`
        : 'Teto atingido · margem zero, política intacta'
  const marginColor = blocked
    ? color.redInk
    : margin === null
      ? color.inkSubtle
      : margin > 0
        ? color.greenInk
        : color.amberInk

  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        background: color.surface,
        border: `1px solid ${color.border}`,
        borderRadius: radius.panel,
        padding: '16px 18px',
        boxShadow: shadow.card,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 14,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Eyebrow style={{ letterSpacing: '.09em' }}>ENVELOPE DE NEGOCIAÇÃO</Eyebrow>
          <Pill
            bg={blocked ? color.redTint : color.brandTint}
            fg={blocked ? color.redDeep : color.brandInk}
            bd={blocked ? color.redBorder : color.brandBorder}
            style={{ transition: 'all .3s ease' }}
          >
            {modalityLabel}
          </Pill>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            fontSize: 12,
            fontWeight: 600,
            color: marginColor,
          }}
        >
          <span
            style={{ width: 8, height: 8, borderRadius: 2, background: marginColor }}
            aria-hidden
          />
          {marginLabel}
        </div>
      </div>

      <div style={{ position: 'relative', height: 96, margin: '0 12px' }}>
        {/* Faixa autorizada (verde) e área fora da política (hachurada). */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 38,
            height: 30,
            borderRadius: radius.sm,
            background: color.surfaceSunken,
            border: `1px solid ${color.border}`,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              background: color.greenTint,
              transition: 'width .5s cubic-bezier(.22,.61,.36,1)',
              width: capWidth,
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              right: 0,
              background: `repeating-linear-gradient(45deg, ${color.redTint} 0 6px, #fdf1ef 6px 12px)`,
              transition: 'left .5s cubic-bezier(.22,.61,.36,1)',
              left: capWidth,
            }}
          />
        </div>

        {/* Parede do teto. */}
        <div
          style={{
            position: 'absolute',
            top: 30,
            height: 46,
            width: 2,
            background: color.red,
            transition: 'left .5s cubic-bezier(.22,.61,.36,1)',
            left: capWidth,
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: 12,
            fontSize: 10.5,
            fontWeight: 700,
            color: color.red,
            transform: 'translateX(-50%)',
            whiteSpace: 'nowrap',
            transition: 'left .5s cubic-bezier(.22,.61,.36,1)',
            left: capWidth,
          }}
        >
          {blocked ? 'NEGOCIAÇÃO BLOQUEADA' : `TETO ${pct(capPct)}`}
        </div>

        {/* Rastro das ofertas anteriores. */}
        {trail.map((value, index) => (
          <div
            key={`${value}-${index}`}
            style={{
              position: 'absolute',
              top: 48,
              width: 9,
              height: 9,
              borderRadius: '50%',
              background: color.brand,
              opacity: 0.28,
              transform: 'translateX(-50%)',
              left: x(value),
            }}
          />
        ))}

        {/* Pedido do cliente. */}
        {clientAskPct !== null ? (
          <div
            style={{
              position: 'absolute',
              top: 34,
              transform: 'translateX(-50%)',
              textAlign: 'center',
              transition: 'left .45s ease',
              left: x(clientAskPct),
            }}
          >
            <div
              style={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                background: color.redTint,
                border: `1.5px solid ${color.red}`,
                color: color.red,
                fontSize: 13,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1,
              }}
            >
              ✕
            </div>
            <div
              style={{
                fontSize: 10.5,
                fontWeight: 700,
                color: color.red,
                marginTop: 5,
                whiteSpace: 'nowrap',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              PEDIDO {pct(clientAskPct)}
            </div>
          </div>
        ) : null}

        {/* Oferta atual. */}
        {offerPct !== null ? (
          <div
            key={offerWall ? `wall-${offerPct}` : `offer-${offerPct}`}
            style={{
              position: 'absolute',
              top: 26,
              transform: 'translateX(-50%)',
              textAlign: 'center',
              transition: 'left .55s cubic-bezier(.34,1.32,.5,1)',
              left: x(offerPct),
              // Ao bater no teto, a oferta "esbarra" na parede em vez de deslizar.
              animation: offerWall ? 'bump .62s cubic-bezier(.3,1.4,.5,1) .52s' : undefined,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: color.ink,
                whiteSpace: 'nowrap',
                fontVariantNumeric: 'tabular-nums',
                marginBottom: 3,
              }}
            >
              OFERTA {pct(offerPct)} · {brl(debt * (1 - offerPct / 100))}
            </div>
            <div
              style={{
                width: 18,
                height: 18,
                borderRadius: '50%',
                background: color.brand,
                border: '3px solid #fff',
                boxShadow: '0 2px 8px rgba(240,134,47,.5)',
                margin: '0 auto',
              }}
            />
          </div>
        ) : null}

        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 74,
            fontSize: 10.5,
            fontWeight: 600,
            color: color.inkSubtle,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          0% · {brl(debt)}
        </div>
        <div
          style={{
            position: 'absolute',
            top: 74,
            transform: 'translateX(-50%)',
            fontSize: 10.5,
            fontWeight: 600,
            color: color.inkSubtle,
            fontVariantNumeric: 'tabular-nums',
            transition: 'left .5s ease',
            left: capWidth,
            whiteSpace: 'nowrap',
          }}
        >
          {pct(capPct)} · {brl(capValue)}
        </div>
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: 74,
            fontSize: 10.5,
            fontWeight: 600,
            color: color.redInk,
          }}
        >
          FORA DA POLÍTICA →
        </div>
      </div>
    </div>
  )
}
